/**
 * useIdleTimeout Hook
 *
 * SECURITY FIX: Monitors user activity and automatically logs out after a period
 * of inactivity. This addresses vulnerability #3 (Improper Session Management)
 * from the security assessment report.
 *
 * Tracks mouse movements, keyboard input, clicks, scrolls, and touch events.
 * After the configured idle period (default: 15 minutes), the user is logged out
 * and redirected to the login page.
 *
 * Usage:
 *   useIdleTimeout(); // Place in App.tsx or a layout component
 */
import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../redux/login/login.action';
import { selectLoginResponse } from '../redux/login/login.selector';
import { loginPages } from '../menu';

// Session timeout in milliseconds (15 minutes)
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

// Warning before timeout in milliseconds (1 minute before timeout)
const WARNING_BEFORE_MS = 1 * 60 * 1000;

// Events that indicate user activity
const ACTIVITY_EVENTS: string[] = [
	'mousedown',
	'mousemove',
	'keydown',
	'scroll',
	'touchstart',
	'click',
	'wheel',
];

const useIdleTimeout = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const loginResponse = useSelector(selectLoginResponse);

	const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isWarningShownRef = useRef<boolean>(false);

	/**
	 * Performs session cleanup: clears Redux state, localStorage, and redirects to login
	 */
	const handleLogout = useCallback(() => {
		dispatch(logout());
		navigate(loginPages.login.path, { replace: true });
		// Show alert about session expiry
		alert('Your session has expired due to inactivity. Please log in again.');
	}, [dispatch, navigate]);

	/**
	 * Shows a warning notification before the session expires
	 */
	const showWarning = useCallback(() => {
		if (!isWarningShownRef.current) {
			isWarningShownRef.current = true;
			// Using a simple console warning; in production this triggers a UI notification
			if (process.env.NODE_ENV === 'development') {
				console.warn('Session will expire in 1 minute due to inactivity.');
			}
		}
	}, []);

	/**
	 * Resets the idle timer on any user activity
	 */
	const resetTimer = useCallback(() => {
		// Clear existing timers
		if (idleTimerRef.current) {
			clearTimeout(idleTimerRef.current);
		}
		if (warningTimerRef.current) {
			clearTimeout(warningTimerRef.current);
		}
		isWarningShownRef.current = false;

		// Set warning timer (fires 1 minute before timeout)
		warningTimerRef.current = setTimeout(showWarning, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

		// Set logout timer
		idleTimerRef.current = setTimeout(handleLogout, IDLE_TIMEOUT_MS);
	}, [handleLogout, showWarning]);

	useEffect(() => {
		// Only track idle timeout when user is logged in
		if (!loginResponse) return;

		// Start the idle timer
		resetTimer();

		// Add event listeners for user activity (throttled via passive listeners)
		ACTIVITY_EVENTS.forEach((event) => {
			document.addEventListener(event, resetTimer, { passive: true });
		});

		// Cleanup on unmount or logout
		return () => {
			ACTIVITY_EVENTS.forEach((event) => {
				document.removeEventListener(event, resetTimer);
			});
			if (idleTimerRef.current) {
				clearTimeout(idleTimerRef.current);
			}
			if (warningTimerRef.current) {
				clearTimeout(warningTimerRef.current);
			}
		};
	}, [loginResponse, resetTimer]);
};

export default useIdleTimeout;
